/**
 * Initial translation catalogs. Seed set covers common UI strings —
 * full sweep across feature pages is a P73-75 polish item.
 *
 * Convention: `feature.element` for keys.
 *   common.* — buttons, generic words
 *   nav.*    — sidebar navigation
 *   auth.*   — login + auth flows
 *   empty.*  — empty-state titles
 */

import type { Locale } from './i18n'

export const catalogs: Record<Locale, Record<string, string>> = {
  en: {
    // Common
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.search': 'Search',
    'common.loading': 'Loading…',
    'common.saving': 'Saving…',
    'common.actions': 'Actions',
    'common.status': 'Status',
    'common.name': 'Name',
    'common.code': 'Code',
    'common.description': 'Description',
    'common.required': 'Required',
    'common.optional': 'Optional',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.confirm': 'Confirm',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.close': 'Close',
    'common.refresh': 'Refresh',
    'common.retry': 'Retry',
    'common.download': 'Download',
    'common.upload': 'Upload',
    'common.export': 'Export',
    'common.import': 'Import',
    'common.page_x_of_y': 'Page {current} of {last}',
    'common.showing_x_to_y_of_z': 'Showing {from}–{to} of {total}',

    // Auth
    'auth.sign_in': 'Sign in',
    'auth.sign_out': 'Sign out',
    'auth.email': 'Email',
    'auth.password': 'Password',
    'auth.remember_me': 'Remember me',
    'auth.forgot_password': 'Forgot password?',
    'auth.login_failed': 'Login failed. Check your credentials.',

    // Navigation
    'nav.dashboard': 'Dashboard',
    'nav.customers': 'Customers',
    'nav.leads': 'Leads',
    'nav.products': 'Products',
    'nav.orders': 'Orders',
    'nav.invoices': 'Invoices',
    'nav.payments': 'Payments',
    'nav.inventory': 'Inventory',
    'nav.purchasing': 'Purchasing',
    'nav.field_force': 'Field Force',
    'nav.visits': 'Visits',
    'nav.service': 'Service',
    'nav.reports': 'Reports',
    'nav.settings': 'Settings',
    'nav.users': 'Users',
    'nav.notifications': 'Notifications',
    'nav.audit_logs': 'Audit Logs',

    // Empty states
    'empty.no_results': 'No results',
    'empty.no_data': 'No data yet',
    'empty.no_notifications': 'No notifications',
    'empty.all_caught_up': "You're all caught up.",

    // Errors
    'error.generic': 'Something went wrong',
    'error.try_again': 'Try again',
    'error.reload_page': 'Reload page',

    // Notifications
    'notifications.title': 'Notifications',
    'notifications.mark_all_read': 'Mark all read',
    'notifications.unread_only': 'Unread only',
    'notifications.read_only': 'Read only',
    'notifications.all': 'All',
    'notifications.preferences': 'Preferences',
    'notifications.feed': 'Feed',
  },

  ur: {
    // Common
    'common.save': 'محفوظ کریں',
    'common.cancel': 'منسوخ کریں',
    'common.delete': 'حذف کریں',
    'common.edit': 'تبدیل کریں',
    'common.create': 'بنائیں',
    'common.search': 'تلاش کریں',
    'common.loading': 'لوڈ ہو رہا ہے…',
    'common.saving': 'محفوظ ہو رہا ہے…',
    'common.actions': 'اعمال',
    'common.status': 'حیثیت',
    'common.name': 'نام',
    'common.code': 'کوڈ',
    'common.description': 'تفصیل',
    'common.required': 'لازمی',
    'common.optional': 'اختیاری',
    'common.yes': 'ہاں',
    'common.no': 'نہیں',
    'common.confirm': 'تصدیق کریں',
    'common.back': 'واپس',
    'common.next': 'اگلا',
    'common.previous': 'پچھلا',
    'common.close': 'بند کریں',
    'common.refresh': 'تازہ کریں',
    'common.retry': 'دوبارہ کوشش',
    'common.download': 'ڈاؤن لوڈ',
    'common.upload': 'اپ لوڈ',
    'common.export': 'برآمد',
    'common.import': 'درآمد',
    'common.page_x_of_y': 'صفحہ {current} از {last}',
    'common.showing_x_to_y_of_z': '{total} میں سے {from}–{to} دکھائے جا رہے ہیں',

    // Auth
    'auth.sign_in': 'سائن ان کریں',
    'auth.sign_out': 'سائن آؤٹ',
    'auth.email': 'ای میل',
    'auth.password': 'پاس ورڈ',
    'auth.remember_me': 'مجھے یاد رکھیں',
    'auth.forgot_password': 'پاس ورڈ بھول گئے؟',
    'auth.login_failed': 'لاگ ان ناکام۔ اپنی تفصیلات چیک کریں۔',

    // Navigation
    'nav.dashboard': 'ڈیش بورڈ',
    'nav.customers': 'گاہک',
    'nav.leads': 'ممکنہ گاہک',
    'nav.products': 'مصنوعات',
    'nav.orders': 'احکامات',
    'nav.invoices': 'انوائسز',
    'nav.payments': 'ادائیگیاں',
    'nav.inventory': 'انوینٹری',
    'nav.purchasing': 'خریداری',
    'nav.field_force': 'فیلڈ فورس',
    'nav.visits': 'دورے',
    'nav.service': 'سروس',
    'nav.reports': 'رپورٹس',
    'nav.settings': 'ترتیبات',
    'nav.users': 'صارفین',
    'nav.notifications': 'اطلاعات',
    'nav.audit_logs': 'آڈٹ لاگز',

    // Empty states
    'empty.no_results': 'کوئی نتیجہ نہیں',
    'empty.no_data': 'ابھی کوئی ڈیٹا نہیں',
    'empty.no_notifications': 'کوئی اطلاع نہیں',
    'empty.all_caught_up': 'سب کچھ پڑھ لیا گیا ہے۔',

    // Errors
    'error.generic': 'کچھ غلط ہو گیا',
    'error.try_again': 'دوبارہ کوشش کریں',
    'error.reload_page': 'صفحہ دوبارہ لوڈ کریں',

    // Notifications
    'notifications.title': 'اطلاعات',
    'notifications.mark_all_read': 'سب پڑھے ہوئے نشان زد کریں',
    'notifications.unread_only': 'صرف غیر پڑھے',
    'notifications.read_only': 'صرف پڑھے ہوئے',
    'notifications.all': 'سب',
    'notifications.preferences': 'ترجیحات',
    'notifications.feed': 'فیڈ',
  },
  // Arabic — keys fall back to English until a translation sweep is done.
  // Direction is already RTL via the RTL_LOCALES list in i18n.tsx.
  ar: {},
}
